import React, { useMemo, useState } from 'react';
import { Loader, Text } from 'src/@vymo/ui/atoms';
import { NoData } from 'src/@vymo/ui/blocks';
import { Keys, locale } from 'src/i18n';
import { useAppSelector } from 'src/store/hooks';
import {
  formatPrimitiveValue,
  getFieldKey,
  isFieldList,
  isFieldMatrix,
  isPrimitiveValue,
} from './queries';
import {
  getPolicyDetailsError,
  getPolicyDetailsLoading,
  getPolicySections,
} from './selectors';
import type { PolicyFieldValue } from './types';
import styles from './index.module.scss';

const renderFieldValue = (value: PolicyFieldValue): React.ReactNode => {
  if (isPrimitiveValue(value)) {
    return (
      <Text type="description" classNames={styles.policyDetails__fieldValue}>
        {formatPrimitiveValue(value)}
      </Text>
    );
  }

  if (isFieldMatrix(value)) {
    return (
      <div className={styles.policyDetails__matrix}>
        {value.map((group, index) => (
          <div
            key={`field-group-${index.toString()}`}
            className={styles.policyDetails__matrixGroup}
          >
            {group.map((item, itemIndex) => (
              <div
                key={getFieldKey(item, itemIndex)}
                className={styles.policyDetails__fieldRow}
              >
                <Text
                  type="sublabel"
                  classNames={styles.policyDetails__fieldLabel}
                >
                  {item.name}
                </Text>
                <div className={styles.policyDetails__inlineFieldValue}>
                  {renderFieldValue(item.value)}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (isFieldList(value)) {
    return (
      <div className={styles.policyDetails__fieldList}>
        {value.map((item, itemIndex) => (
          <div
            key={getFieldKey(item, itemIndex)}
            className={styles.policyDetails__fieldRow}
          >
            <Text type="sublabel" classNames={styles.policyDetails__fieldLabel}>
              {item.name}
            </Text>
            <div className={styles.policyDetails__inlineFieldValue}>
              {renderFieldValue(item.value)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

function PolicyDetails() {
  const policySections = useAppSelector(getPolicySections);
  const isLoading = useAppSelector(getPolicyDetailsLoading);
  const errorMessage = useAppSelector(getPolicyDetailsError);
  const validSections = useMemo(
    () =>
      policySections.filter(
        (section) => section && section.name && Array.isArray(section.value),
      ),
    [policySections],
  );
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});

  if (isLoading) {
    return (
      <div className={styles.policyDetails}>
        <Loader visible fullPage={false} />
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className={styles.policyDetails}>
        <NoData
          message={errorMessage}
          classNames={styles.policyDetails__status}
        />
      </div>
    );
  }

  if (!policySections.length) {
    return (
      <div className={styles.policyDetails}>
        <NoData
          message={locale(Keys.NO_POLICY_DETAILS_FOUND)}
          classNames={styles.policyDetails__status}
        />
      </div>
    );
  }

  return (
    <div className={styles.policyDetails} data-test-id="policy-details-page">
      <div className={styles.policyDetails__header}>
        <Text type="h5">{locale(Keys.POLICY_DETAILS)}</Text>
      </div>

      <div className={styles.policyDetails__content}>
        <div className={styles.policyDetails__panel}>
          {validSections.map((section) => {
            const isOpen = Boolean(expandedSections[section.name]);
            return (
              <div
                key={section.name}
                className={styles.policyDetails__sectionItem}
              >
                <button
                  type="button"
                  className={styles.policyDetails__sectionRow}
                  onClick={() =>
                    setExpandedSections((prev) => ({
                      ...prev,
                      [section.name]: !prev[section.name],
                    }))
                  }
                >
                  <Text
                    type="label"
                    classNames={styles.policyDetails__sectionName}
                  >
                    {section.name}
                  </Text>
                  <div className={styles.policyDetails__sectionMeta}>
                    <Text
                      type="sublabel"
                      classNames={styles.policyDetails__sectionDetail}
                    >
                      {section.value.length ? locale(Keys.DETAIL) : ''}
                    </Text>
                    <Text
                      type="label"
                      classNames={styles.policyDetails__sectionChevron}
                    >
                      {isOpen ? 'v' : '>'}
                    </Text>
                  </div>
                </button>

                {isOpen && (
                  <div className={styles.policyDetails__expandedPanel}>
                    <div className={styles.policyDetails__sectionBody}>
                      {section.value.map((field, fieldIndex) => (
                        <div
                          className={styles.policyDetails__fieldRow}
                          key={getFieldKey(field, fieldIndex)}
                        >
                          <div className={styles.policyDetails__fieldRow__name}>
                            <Text
                              type="label"
                              classNames={styles.policyDetails__fieldLabel}
                            >
                              {field.name}
                            </Text>
                          </div>
                          <div
                            className={styles.policyDetails__fieldRow__content}
                          >
                            {renderFieldValue(field.value)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default PolicyDetails;
